import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PropTypesInput, PropTypesMeta } from '@zedoc/react-questionnaire';
import { useTranslation } from 'react-i18next';
import FormFieldWrapper from '../FormFieldWrapper';
import Select from '../../Select';
import Input from '../../Input';
import { getCountryCodes, getCountryPrefix, parsePhoneNumber } from './helpers';
import useValidateStatus from '../../../utils/useValidateStatus';
import InputGroup from '../../InputGroup';

const FormFieldPhoneNumber = ({
  'data-testid': testId,
  label,
  required,
  showValueOnly,
  disabled,
  autoFocus,
  defaultCountry,
  placeholder,
  input,
  meta,
}) => {
  const { t } = useTranslation('forms');
  const validateStatus = useValidateStatus({ meta, required });

  const { onChange } = input;
  const { value } = input;
  const countryCodes = useMemo(() => getCountryCodes(), []);
  const phoneNumber = parsePhoneNumber(value) || {};
  const countryCode =
    phoneNumber.iso || defaultCountry || countryCodes[0].value;
  const localNumber = phoneNumber.localNumber || '';

  const handleChangeCountry = (code) => {
    const countryPrefix = getCountryPrefix(code);
    onChange(countryPrefix + localNumber);
  };

  const handleChangeLocalNumber = (event) => {
    const rawValue = event.target.value;
    const sanitizedLocalNumber = rawValue.replace(/\D/g, ''); // only digits
    const normalizedLocalNumber = sanitizedLocalNumber.replace(/^0+/, ''); // remove leading zeros

    const countryPrefix = getCountryPrefix(countryCode);
    onChange(countryPrefix + normalizedLocalNumber);
  };

  return (
    <FormFieldWrapper
      label={label}
      required={required}
      meta={meta}
      disabled={disabled}
      isNewPresentation
    >
      {showValueOnly ? (
        input.value || <span>&ndash;</span>
      ) : (
        <InputGroup>
          <Select
            data-testid={`${testId}-country-select`}
            value={countryCode}
            options={countryCodes}
            onChange={handleChangeCountry}
            inputGroupChildProps={{
              noGrow: true,
            }}
            isDisabled={disabled}
          />
          <Input
            data-testid={testId}
            inputMode="tel"
            value={localNumber}
            onChange={handleChangeLocalNumber}
            disabled={disabled}
            autoFocus={autoFocus}
            placeholder={placeholder || t('phone.noLeadingZero')}
            validateStatus={validateStatus}
          />
        </InputGroup>
      )}
    </FormFieldWrapper>
  );
};

FormFieldPhoneNumber.propTypes = {
  input: PropTypesInput.isRequired,
  meta: PropTypesMeta.isRequired,
  'data-testid': PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  showValueOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  defaultCountry: PropTypes.string,
};

FormFieldPhoneNumber.defaultProps = {
  'data-testid': 'form-field-phone-number',
  label: null,
  required: false,
  showValueOnly: false,
  disabled: false,
  autoFocus: false,
  placeholder: null,
  defaultCountry: null,
};

export default FormFieldPhoneNumber;
